import { useState } from "react";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AuthModal({ open, onOpenChange, onLogin, onRegister }) {
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setEmail(""); setPassword(""); setError(""); setBusy(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    setError("");
    try {
      if (tab === "login") {
        await onLogin(email.trim(), password);
      } else {
        await onRegister(email.trim(), password);
      }
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-surface border-white/10 text-foreground max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {tab === "login" ? "Accedi" : "Registrati"}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-6">
          {[{ id: "login", label: "Accedi", Icon: LogIn }, { id: "register", label: "Registrati", Icon: UserPlus }].map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => { setTab(id); setError(""); }}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === id ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input-luxury w-full text-base py-2"
              placeholder="mario@esempio.it"
              data-testid="auth-email"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete={tab === "login" ? "current-password" : "new-password"}
              className="input-luxury w-full text-base py-2"
              placeholder="Minimo 8 caratteri"
              data-testid="auth-password"
            />
          </div>

          {error && (
            <p className="text-red-400 text-xs">{error}</p>
          )}

          <Button
            type="submit"
            disabled={busy || !email.trim() || !password}
            className="w-full rounded-none bg-white text-black hover:bg-gold hover:text-black transition-all duration-300 py-5 text-sm uppercase tracking-[0.22em] font-medium disabled:opacity-50"
            data-testid="auth-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : tab === "login" ? "Accedi" : "Crea account"}
          </Button>

          {/* Google OAuth placeholder */}
          <div className="relative flex items-center gap-3 my-2">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] text-white/30 uppercase tracking-widest">oppure</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <Button
            type="button"
            disabled
            className="w-full rounded-none border border-white/10 text-white/30 py-5 text-xs uppercase tracking-[0.18em] cursor-not-allowed"
            title="Prossimamente"
          >
            Continua con Google — prossimamente
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
